import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Calendar, Clock } from 'lucide-react';
import EventHero from '../components/events/EventHero';
import EventHeader from '../components/events/EventHeader';
import EventDescription from '../components/events/EventDescription';
import EventBonuses from '../components/events/EventBonuses';
import RaidBossesSection from '../components/events/RaidBossesSection';
import WildEncountersSection from '../components/events/WildEncountersSection';
import EggHatchesSection from '../components/events/EggHatchesSection';
import FieldResearchSection from '../components/events/FieldResearchSection';
import LanguageToggle from '../components/shared/LanguageToggle';
import BlockRenderer from '../components/events/blocks/BlockRenderer';
import { PageContainer, SectionHeader } from '../components/shared';
import { useLanguage } from '../contexts/LanguageContext';

const EventDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language, setLanguage } = useLanguage();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    fetchEventDetail();
  }, [id]);

  const fetchEventDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${getApiBaseUrl()}/api/events/${id}`);
      const data = await response.json();

      if (data.success) {
        setEvent(data.data);
      } else {
        setError(data.error || 'ไม่พบข้อมูลกิจกรรม');
      }
    } catch (error) {
      console.error('Error fetching event detail:', error);
      setError('เกิดข้อผิดพลาดในการโหลดข้อมูล');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('events.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'ไม่พบข้อมูลกิจกรรม'}</p>
          <button
            onClick={() => navigate('/calendar')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            กลับไปหน้าตารางกิจกรรม
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Image Section */}
      <EventHero event={event} />

      {/* Main Content */}
      <PageContainer maxWidth="4xl" padding={true} className="py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
          className="space-y-8"
        >
          {/* Section Header with Back Button */}
          <SectionHeader
            title={event.title_en || event.title || 'Event Details'}
            subtitle={event.title_th}
            backButton={{
              onClick: () => navigate(-1),
              label: t('events.back'),
              icon: <ArrowLeft className="w-4 h-4" />
            }}
            actions={[
              <LanguageToggle key="lang" language={language} onLanguageChange={setLanguage} />
            ]}
          />
          
          {/* Event Header (Date, Status) */}
          <EventHeader event={event} language={language} />

          {/* Block-Based Content (New System) */}
          {event.content_blocks && Array.isArray(event.content_blocks) && event.content_blocks.length > 0 ? (
            <BlockRenderer blocks={event.content_blocks} language={language} />
          ) : (
            <>
              {/* Legacy Content (Backward Compatibility) */}
              {/* Description Section */}
              <EventDescription event={event} language={language} />

              {/* Event Bonuses */}
              {event.event_bonuses && event.event_bonuses.length > 0 && (
                <EventBonuses bonuses={event.event_bonuses} />
              )}

              {/* Raid Bosses Section */}
              {event.raid_bosses && event.raid_bosses.length > 0 && (
                <RaidBossesSection raidBosses={event.raid_bosses} />
              )}

              {/* Wild Encounters Section */}
              {event.wild_encounters && event.wild_encounters.length > 0 && (
                <WildEncountersSection wildEncounters={event.wild_encounters} />
              )}

              {/* Egg Hatches Section */}
              {event.egg_hatches && event.egg_hatches.length > 0 && (
                <EggHatchesSection eggHatches={event.egg_hatches} />
              )}

              {/* Field Research Section */}
              {event.field_research && event.field_research.length > 0 && (
                <FieldResearchSection fieldResearch={event.field_research} />
              )}
            </>
          )}
        </motion.div>
      </PageContainer>
    </div>
  );
};

export default EventDetailPage;
